import React from 'react';
import {
  Button,
  Divider,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Text,
  TextProps,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { CalendarBlank, CaretDown } from 'phosphor-react';

import { FIN, MESES_ABBR, RADII } from './theme';

interface PeriodPickerProps {
  range: { iniM: number; iniY: number; fimM: number; fimY: number };
  /** seta o período por mês/ano (com clamp: início ≤ fim). */
  onMonths: (iniM: number, iniY: number, fimM: number, fimY: number) => void;
  atalhos: Record<string, () => void>;
  accent?: string;
  anos?: number[];
}

const yy = (y: number) => String(y).slice(2);

const Eyebrow = (props: TextProps) => (
  <Text
    fontSize="10px"
    fontWeight={700}
    letterSpacing="0.08em"
    color={FIN.faint}
    textTransform="uppercase"
    {...props}
  />
);

const ATALHOS: [string, string][] = [
  ['esteAno', 'Este ano'],
  ['anoPassado', 'Ano passado'],
  ['primSem', '1º sem'],
  ['segSem', '2º sem'],
  ['trimAtual', 'Trimestre'],
  ['ult12m', 'Últ. 12m'],
];

export const PeriodPicker = ({
  range,
  onMonths,
  atalhos,
  accent = FIN.info,
  anos,
}: PeriodPickerProps) => {
  const anoAtual = new Date().getFullYear();
  const ANOS = anos ?? [anoAtual - 2, anoAtual - 1, anoAtual, anoAtual + 1];
  const label = `${MESES_ABBR[range.iniM]}/${yy(range.iniY)} – ${MESES_ABBR[range.fimM]}/${yy(range.fimY)}`;

  const MonthYear = ({
    m,
    y,
    onSet,
  }: {
    m: number;
    y: number;
    onSet: (m: number, y: number) => void;
  }) => (
    <Flex gap={2}>
      <Select
        size="sm"
        flex="1"
        borderRadius={RADII.inp}
        value={m}
        onChange={e => onSet(+e.target.value, y)}
      >
        {MESES_ABBR.map((mm, i) => (
          <option key={mm} value={i}>
            {mm}
          </option>
        ))}
      </Select>
      <Select
        size="sm"
        w="84px"
        borderRadius={RADII.inp}
        value={y}
        onChange={e => onSet(m, +e.target.value)}
      >
        {ANOS.map(yr => (
          <option key={yr} value={yr}>
            {yr}
          </option>
        ))}
      </Select>
    </Flex>
  );

  return (
    <Popover placement="bottom-end" closeOnBlur>
      <PopoverTrigger>
        <Button
          size="sm"
          variant="outline"
          borderRadius={RADII.pill}
          borderColor={FIN.line}
          _hover={{ borderColor: FIN.lineHover }}
          leftIcon={<CalendarBlank size={16} weight="duotone" color={accent} />}
          rightIcon={<CaretDown size={12} />}
          fontWeight={700}
          color={FIN.ink}
          whiteSpace="nowrap"
        >
          {label}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w="320px"
          borderRadius={RADII.card}
          boxShadow="0 8px 30px rgba(20,40,60,0.14)"
          zIndex={1500}
        >
          <PopoverArrow />
          <PopoverBody p={4}>
            <Eyebrow mb={2} letterSpacing="0.1em">
              Período — De / Até
            </Eyebrow>
            <Eyebrow mb={1}>De</Eyebrow>
            <MonthYear
              m={range.iniM}
              y={range.iniY}
              onSet={(m, y) => onMonths(m, y, range.fimM, range.fimY)}
            />
            <Eyebrow mt={3} mb={1}>
              Até
            </Eyebrow>
            <MonthYear
              m={range.fimM}
              y={range.fimY}
              onSet={(m, y) => onMonths(range.iniM, range.iniY, m, y)}
            />
            <Divider my={3} />
            <Eyebrow mb={2} letterSpacing="0.1em">
              Atalhos
            </Eyebrow>
            <Wrap spacing={2}>
              {ATALHOS.map(([k, lbl]) => (
                <WrapItem key={k}>
                  <Button
                    size="xs"
                    variant="outline"
                    borderRadius="md"
                    fontWeight={700}
                    color={FIN.muted}
                    borderColor={FIN.line}
                    _hover={{ borderColor: accent, color: accent }}
                    onClick={atalhos[k]}
                  >
                    {lbl}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
